import { useEffect } from 'react';
import { useSocket } from '../context/SocketContext';
import { notificationEventBus } from '../lib/notificationEventBus';

/**
 * useNotificationSync - Hook to synchronize notification state across all pages and components
 *
 * This hook listens to both Socket.IO events (for real-time notifications from server)
 * and local event bus events (for same-page sync).
 *
 * Usage:
 * ```tsx
 * useNotificationSync({
 *   onNew: (notification) => {
 *     setNotifications(prev => [notification, ...prev]);
 *     setUnreadCount(prev => prev + 1);
 *   },
 *   onRead: (notificationId) => {
 *     setNotifications(prev => prev.map(n => n.id === notificationId ? {...n, read: true} : n));
 *     setUnreadCount(prev => Math.max(0, prev - 1));
 *   },
 *   onReadAll: () => {
 *     setNotifications(prev => prev.map(n => ({...n, read: true})));
 *     setUnreadCount(0);
 *   },
 *   onDelete: (notificationId) => {
 *     setNotifications(prev => prev.filter(n => n.id !== notificationId));
 *   }
 * });
 * ```
 */

interface UseNotificationSyncOptions {
  /**
   * Called when a new notification is received
   */
  onNew?: (notification: any) => void;

  /**
   * Called when a notification is marked as read
   */
  onRead?: (notificationId: string) => void;

  /**
   * Called when all notifications are marked as read
   */
  onReadAll?: () => void;

  /**
   * Called when a notification is deleted
   */
  onDelete?: (notificationId: string) => void;
}

export function useNotificationSync(options: UseNotificationSyncOptions) {
  const { socket } = useSocket();
  const { onNew, onRead, onReadAll, onDelete } = options;

  useEffect(() => {
    // === Socket.IO Listeners (for real-time notifications from server) ===
    if (socket) {
      const handleSocketNotification = (notification: any) => {
        if (onNew) {
          onNew(notification);
        }
      };

      const handleSocketNotificationRead = (data: { notificationId: string }) => {
        if (onRead) {
          onRead(data.notificationId);
        }
      };

      const handleSocketNotificationReadAll = () => {
        if (onReadAll) {
          onReadAll();
        }
      };

      const handleSocketNotificationDelete = (data: { notificationId: string }) => {
        if (onDelete) {
          onDelete(data.notificationId);
        }
      };

      socket.on('notification', handleSocketNotification);
      socket.on('notification:read', handleSocketNotificationRead);
      socket.on('notification:readAll', handleSocketNotificationReadAll);
      socket.on('notification:delete', handleSocketNotificationDelete);

      return () => {
        socket.off('notification', handleSocketNotification);
        socket.off('notification:read', handleSocketNotificationRead);
        socket.off('notification:readAll', handleSocketNotificationReadAll);
        socket.off('notification:delete', handleSocketNotificationDelete);
      };
    }
  }, [socket, onNew, onRead, onReadAll, onDelete]);

  useEffect(() => {
    // === Event Bus Listeners (for same-page sync) ===
    const handleBusNew = (data: any) => {
      if (onNew) {
        onNew(data.notification);
      }
    };

    const handleBusRead = (data: any) => {
      if (onRead) {
        onRead(data.notificationId);
      }
    };

    const handleBusReadAll = (data: any) => {
      if (onReadAll) {
        onReadAll();
      }
    };

    const handleBusDelete = (data: any) => {
      if (onDelete) {
        onDelete(data.notificationId);
      }
    };

    const unsubNew = notificationEventBus.on('notification:new', handleBusNew);
    const unsubRead = notificationEventBus.on('notification:read', handleBusRead);
    const unsubReadAll = notificationEventBus.on('notification:readAll', handleBusReadAll);
    const unsubDelete = notificationEventBus.on('notification:delete', handleBusDelete);

    return () => {
      unsubNew();
      unsubRead();
      unsubReadAll();
      unsubDelete();
    };
  }, [onNew, onRead, onReadAll, onDelete]);
}
